import { omit } from "lodash";

import {
  formatValidQueryParamUrl,
  formatValidQueryParamUrlCommaSeparated
} from "utils/common";
import { formatProductCatalogOption } from "utils/products.utils";
import { viteEnv } from "utils/vite-env.util";

import { createPaginatedEndpoint } from "../utils/services.util";
import { emptySplitApi } from "./empty-split-api";
import {
  CatalogCSVPreviewSummary,
  CatalogCSVUploadResponse,
  Product,
  ProductBulkAssignCPTCodesInput,
  ProductBulkAssignCPTCodesResponse,
  ProductBulkUpdateInput,
  ProductBulkUpdateResponse,
  ProductCSVQuery,
  ProductCatalogOption,
  ProductInput,
  ProductsFilters,
  ProductsResponse
} from "./types/products.type";
import { CSVReportResponse } from "./types/reports.type";

export const DEFAULT_PRODUCTS_LIMIT = 25;

// RestQL selector for the optional Product.cpt_codes M2M field.
// Gated behind multipleCptCodesFeature to avoid querying a field the BE may
// not expose yet (deploy-ordering safety).
const cptCodesSelector = (multipleCptCodesFeature?: boolean): string =>
  multipleCptCodesFeature ? ",cpt_codes{id,code,description}" : "";

export const getProductListQuery = ({
  multipleCptCodes,
  productCategories
}: {
  multipleCptCodes?: boolean;
  productCategories?: boolean;
} = {}): string => {
  const cptCodes = cptCodesSelector(multipleCptCodes);
  const category = productCategories ? ",category" : "";
  return `{id,name,display_name,starred,repair_eligible,specification,auto_delivery,master_product{id,cpt_code{id,code,description}${cptCodes},type,subtype,description,specification},price,is_active,managed_stock,supplier_price,max_discount,cpt_code{id,code,description}${cptCodes},type,taxable,income_account,expense_account,tier,has_picture${category}}`;
};

export type CatalogCounts = {
  all: number;
  services: number;
  hearing_aids: number;
  earmolds: number;
  accessories: number;
  fees: number;
  supplies: number;
  inactive: number;
};

export const productsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getProduct: builder.query<
      Product,
      { id: number; multipleCptCodesFeature?: boolean }
    >({
      query: ({ id, multipleCptCodesFeature }) => {
        const cptCodes = cptCodesSelector(multipleCptCodesFeature);
        return `/products/${id}/?query={*,cpt_code{id,code,description}${cptCodes},master_product{*,cpt_code{id,code,description}${cptCodes}}}`;
      },
      providesTags: ["Products"]
    }),
    getProducts: builder.query<Product[], ProductsFilters>({
      query: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
        `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
          ...filterParams,
          search: search ? encodeURIComponent(search) : undefined
        })}&query=${query}`,
      transformResponse: (response: ProductsResponse) => {
        return response.results;
      },
      providesTags: ["Products"]
    }),
    getProductsCount: builder.query<number, ProductsFilters>({
      query: ({ query = "{id}", limit, offset, ...filterParams }) =>
        `/products/?limit=0&offset=0&${formatValidQueryParamUrlCommaSeparated(
          filterParams
        )}&query=${query}`,
      transformResponse: (response: { count: number }) => {
        return response.count || 0;
      },
      providesTags: ["Products"]
    }),
    getProductsPaginated: createPaginatedEndpoint<ProductsFilters, Product[]>(
      builder,
      {
        queryFn: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
          `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
            ...filterParams,
            search: search && encodeURIComponent(search)
          })}&query=${query}`,
        transformResponseFn: (response: ProductsResponse) => response.results
      }
    ),
    getProductCatalogOptions: builder.query<
      ProductCatalogOption[],
      ProductsFilters
    >({
      query: ({ query = "{*}", limit = 300, search, ...filterParams }) =>
        `/products/?limit=${limit}&${formatValidQueryParamUrlCommaSeparated({
          ...filterParams,
          search: search && encodeURIComponent(search)
        })}&query=${query}`,
      transformResponse: (response: ProductsResponse) => {
        return formatProductCatalogOption(response.results);
      },
      providesTags: ["Products"]
    }),
    queryProductsCounter: builder.query<
      number,
      Omit<ProductsFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/products/count/?${formatValidQueryParamUrlCommaSeparated({
          ...filters
        })}`,
      providesTags: ["Products"]
    }),
    getCatalogCounts: builder.query<
      CatalogCounts,
      { include_migrated?: boolean }
    >({
      query: ({ include_migrated }) =>
        `/products/counts/?${formatValidQueryParamUrlCommaSeparated({
          include_migrated
        })}`,
      providesTags: ["Products"]
    }),
    addProduct: builder.mutation<Product, ProductInput>({
      query: (product) => ({
        url: "/products/",
        method: "POST",
        body: product
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    editProduct: builder.mutation<Product, ProductInput>({
      query: (product) => ({
        url: `/products/${product.id}/`,
        method: "PATCH",
        body: omit(product, "id")
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    bulkAssignProductCPTCodes: builder.mutation<
      ProductBulkAssignCPTCodesResponse,
      ProductBulkAssignCPTCodesInput
    >({
      query: ({ filters, primary_cpt_code, secondary_cpt_code }) => {
        const filterParams = omit(filters, ["query", "limit", "offset"]);
        return {
          url: `/products/bulk-assign-cpt-codes/?${formatValidQueryParamUrlCommaSeparated(
            {
              ...filterParams,
              search: filters.search && encodeURIComponent(filters.search)
            }
          )}`,
          method: "POST",
          body: { primary_cpt_code, secondary_cpt_code }
        };
      },
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    addProductToClinicBulk: builder.mutation<
      Product,
      {
        clinic_ids: number[];
        product_ids: number[];
      }
    >({
      query: (body) => ({
        url: "/products-bulk-add/",
        method: "POST",
        body: body
      }),
      invalidatesTags: ["Products"]
    }),
    updateProductClinics: builder.mutation<
      Product,
      {
        productId: number;
        clinics: {
          add?: number[];
          remove?: number[];
        };
      }
    >({
      query: ({ productId, ...body }) => ({
        url: `/products/${productId}/`,
        method: "PATCH",
        body: body
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),

    bulkDeactivateProducts: builder.mutation<
      { deactivated_ids: number[] },
      { ids: number[] }
    >({
      query: (body) => ({
        url: "/products/bulk-deactivate/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),

    // Multipart PATCH for the product picture (PNG/JPG ≤500KB, enforced BE-side
    // too). Append the file as `picture`, or an empty string to clear it.
    updateProductPicture: builder.mutation<
      Product,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/products/${id}/`,
        method: "PATCH",
        body: formData
      }),
      invalidatesTags: ["Products"]
    }),
    uploadProductsCatalogCSV: builder.mutation<
      CatalogCSVUploadResponse,
      FormData
    >({
      query: (body) => ({
        url: "/products/bulk-update-from-csv/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    // In-app bulk edit (ENG-1911 phase 3 / SN-16649): one set of field values
    // applied to up to 500 practice products in a single SQL update.
    bulkUpdateProducts: builder.mutation<
      ProductBulkUpdateResponse,
      ProductBulkUpdateInput
    >({
      query: (body) => ({
        url: "/products/bulk-update/",
        method: "POST",
        body
      }),
      invalidatesTags: [
        "Products",
        "MergeTagsPatient",
        "MergeTagsPatientProduct"
      ]
    }),
    // Dry-run of the catalog CSV upload (ENG-1911 phase 2): same validation, zero
    // writes, returns the itemized old->new diff. Read-only, so no tag invalidation.
    previewProductsCatalogCSV: builder.mutation<
      CatalogCSVPreviewSummary,
      FormData
    >({
      query: (body) => ({
        url: "/products/preview-update-from-csv/",
        method: "POST",
        body
      })
    }),
    downloadProductsCSV: builder.query<CSVReportResponse, ProductCSVQuery>({
      query: (filters) => ({
        url: `${
          viteEnv.VITE_SUNO_API
        }/products/?${formatValidQueryParamUrl({ ...filters, search: filters.search && encodeURIComponent(filters.search) })}`,
        headers: { Accept: "custom-csv/json" }
      })
    })
  })
});

export const {
  useGetProductsQuery,
  useGetProductsCountQuery,
  useGetProductQuery,
  useGetProductCatalogOptionsQuery,
  useAddProductMutation,
  useEditProductMutation,
  useBulkAssignProductCPTCodesMutation,
  useQueryProductsCounterQuery,
  useLazyQueryProductsCounterQuery,
  useGetCatalogCountsQuery,
  useAddProductToClinicBulkMutation,
  useUpdateProductClinicsMutation,
  useUpdateProductPictureMutation,
  useBulkDeactivateProductsMutation,
  useUploadProductsCatalogCSVMutation,
  usePreviewProductsCatalogCSVMutation,
  useBulkUpdateProductsMutation,
  useLazyDownloadProductsCSVQuery
} = productsApi;
